import type { ComboboxItem } from "@mantine/core"
import { useCallback } from "react"

import type { BusinessPartnerRepositorySuggestionResponseDto } from "~/gen/api/types/BusinessPartnerRepositorySuggestionResponseDto.ts"
import {
  doesBusinessPartnerRepositorySuggestionMatchSearch,
  isStrongBusinessPartnerRepositorySuggestion,
  MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS,
} from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/utils.ts"

/** Sentinel `ComboboxItem.value` for the directory opt-out row (not a repository record id). */
export const BP_REPO_CREATE_NEW_SENTINEL = "__bp_repo_create_anyway__" as const

export type BusinessPartnerCreateNewRowOption = ComboboxItem & {
  readonly kind: "createAnyway"
}

export type BpRepoSuggestionAutocompleteOption = ComboboxItem & {
  readonly suggestedBusinessPartner: BusinessPartnerRepositorySuggestionResponseDto
}

const isBpRepoSuggestionAutocompleteOption = (
  option: ComboboxItem
): option is BpRepoSuggestionAutocompleteOption => {
  return Reflect.has(option, "suggestedBusinessPartner")
}

export type UseBusinessPartnerRepositoryAutocompletePrependOptionsReturn = {
  /** `remoteOptions` may include non-suggestion rows; only directory suggestions are considered. */
  readonly prependOptions: (args: {
    searchValue: string
    remoteOptions: readonly ComboboxItem[]
  }) => readonly BusinessPartnerCreateNewRowOption[]
}

export const useBusinessPartnerRepositoryAutocompletePrependOptions =
  (): UseBusinessPartnerRepositoryAutocompletePrependOptionsReturn => {
    const prependOptions = useCallback(
      ({
        searchValue,
        remoteOptions,
      }: {
        searchValue: string
        remoteOptions: readonly ComboboxItem[]
      }): readonly BusinessPartnerCreateNewRowOption[] => {
        const trimmed = searchValue.trim()
        if (trimmed.length < MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS) {
          return []
        }
        const visibleDirectorySuggestions = remoteOptions
          .filter(isBpRepoSuggestionAutocompleteOption)
          .filter((option) =>
            doesBusinessPartnerRepositorySuggestionMatchSearch(
              option.suggestedBusinessPartner,
              searchValue
            )
          )
        const hasStrongDirectoryMatch = visibleDirectorySuggestions.some(
          (option) =>
            isStrongBusinessPartnerRepositorySuggestion(
              option.suggestedBusinessPartner
            )
        )
        if (!hasStrongDirectoryMatch) {
          return []
        }
        return [
          {
            value: BP_REPO_CREATE_NEW_SENTINEL,
            label: trimmed,
            kind: "createAnyway",
          },
        ]
      },
      []
    )

    return { prependOptions }
  }
